import React, { useState } from 'react';
import { observer } from 'mobx-react';
import classes from 'classnames';

import useStores from '../../hooks/useStores';
import postData from '../../webservices/submitForm';

import { PREFERRED_COMMUNICATION_TYPES, REQUEST_TYPES } from '../../models/form.model';

import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Confirmation from '../../components/Confirmation';
import Disclaimer from '../../components/Disclaimer';
import Input, { PhoneInput } from '../../components/Input';
import StateSelect from '../../components/StateSelect';
import Toast from '../../components/Toast'

import styles from './CCPAForm.module.scss';

import disclaimers from '../../TDAs';
import { lookupTable } from '../../TDAs/constants';
import NotFound from '../NotFound';

interface CCPAFormProps {
  tdaContext: string;
}

const CCPAForm: React.FC<CCPAFormProps> = ({ tdaContext }) => {
  const [confirmation, setConfirmation] = useState(false);
  const [toastActive, setToastActive] = useState(false)
  const [toastText, setToastText] = useState('')

  const {
    formStore: {
      requestType,
      namePrefix,
      nameFirst,
      nameMiddle,
      nameLast,
      nameSuffix,
      contactEmail,
      contactPhone,
      addressLine1,
      addressLine2,
      addressCity,
      addressState,
      addressZIP,
      contactBy,
      acceptDisclaimer,

      currentStep,

      updateForm,
    },
  } = useStores();

  const currentTDA = lookupTable[tdaContext.toLowerCase()];

  if (!currentTDA) {
    return <NotFound />;
  }

  // get TDA from url
  // get disclaimer text from here
  const thisDisclaimer = disclaimers[currentTDA];

  const handleInputChange = (name: string, newValue: string | null) => {
    updateForm({ [name]: newValue });
  };

  const handleOptionSelect = (optionKey: string, optionVal: string) => {
    updateForm({ [optionKey]: optionVal });
  };

  const submitForm = () => {
    // TODO: move this somewhere else?
    const formData = {
      requestType,
      namePrefix,
      nameFirst,
      nameMiddle,
      nameLast,
      nameSuffix,
      contactEmail,
      contactPhone,
      addressLine1,
      addressLine2,
      addressCity,
      addressState,
      addressZIP,
      contactBy,
      acceptDisclaimer,
      TDA: currentTDA,
    };
    return postData(`${process.env.REACT_APP_API_URL}/ccpa`, formData)
    .then((data) => {
      return setConfirmation(true)
    }).catch((err) => {
      setToastActive(true)
      setToastText('An unknown error has occurred. Please try again later')
    });

  };

  // form fades in as we progress
  const stepDisabled: any = {
    0: false,
    1: 1 > currentStep,
    2: 2 > currentStep,
    3: 3 > currentStep,
    4: 4 > currentStep,
    5: 5 > currentStep,
  };

  const stepClasses = (classNames: string = '', stepNum: number = 0) => {
    return classes(classNames, styles.isFormStep, { [styles.hideFormStep]: stepDisabled[stepNum] });
  };

  if (confirmation) {
    return <Confirmation />;
  }

  return (
    <div className={styles.pageContainer}>
      <Toast active={toastActive} onClick={e => setToastActive(false)} text={toastText}></Toast>
      <div className={styles.headerContainer}>
        <h1>Personal Information Request</h1>
      </div>
      <div className={styles.pageInner}>
        <div className={`${styles.formContainer} ie-height-fix`}>
          <div className={styles.requestTypeContainer}>
            <div className={styles.subHeaderContainerTop}>
              <h2>Request Type</h2>
              <p>One rights request per submission. Requests will be processed in their order of submission.</p>
            </div>
            <Button
              active={requestType === REQUEST_TYPES.REQUEST_COPY}
              disabled={false}
              onClick={() => handleOptionSelect('requestType', REQUEST_TYPES.REQUEST_COPY)}
              text={'Get my personal information'}
              type={'Round'}
            />
            <Button
              active={requestType === REQUEST_TYPES.DO_NOT_SELL}
              className={styles.requestTypeCenterButton}
              disabled={false}
              onClick={() => handleOptionSelect('requestType', REQUEST_TYPES.DO_NOT_SELL)}
              text={'Do not sell my personal information'}
              type={'Round'}
            />
            <Button
              active={requestType === REQUEST_TYPES.DELETE}
              disabled={false}
              onClick={() => handleOptionSelect('requestType', REQUEST_TYPES.DELETE)}
              text={'Delete my personal information'}
              type={'Round'}
            />
          </div>
          <div className={stepClasses(styles.subHeaderContainer, 1)}>
            <h2>Personal Information</h2>
          </div>
          <div className={stepClasses(styles.firstNameContainer, 1)}>
            <div className={styles.firstNameInputContainer}>
              <Input disabled={stepDisabled[1]} label={'First Name'} name={'nameFirst'} onChange={handleInputChange} required={true} value={nameFirst} />
            </div>
            <div className={styles.prefixContainer}>
              <Input disabled={stepDisabled[1]} label={'Prefix'} name={'namePrefix'} onChange={handleInputChange} value={namePrefix} />
            </div>
          </div>
          <div className={stepClasses(styles.middleInitialContainer, 1)}>
            <Input disabled={stepDisabled[1]} label={'Middle Initial'} name={'nameMiddle'} onChange={handleInputChange} value={nameMiddle} />
          </div>
          <div className={stepClasses(styles.lastNameContainer, 1)}>
            <div className={styles.lastNameInputContainer}>
              <Input disabled={stepDisabled[1]} label={'Last Name'} name={'nameLast'} onChange={handleInputChange} required={true} value={nameLast} />
            </div>
            <div className={styles.suffixContainer}>
              <Input disabled={stepDisabled[1]} label={'Suffix'} name={'nameSuffix'} onChange={handleInputChange} value={nameSuffix} />
            </div>
          </div>
          <div className={stepClasses(styles.subHeaderContainer, 2)}>
            <h2>Contact Information</h2>
            <p>
              All fields required<span>*</span>
            </p>
          </div>
          <div className={stepClasses(styles.contactByContainer, 2)}>
            <span>Contact me by</span>
            <div className={styles.contactByEmail}>
              <Checkbox
                checked={PREFERRED_COMMUNICATION_TYPES.EMAIL === contactBy}
                disabled={stepDisabled[2]}
                label="Email"
                name="contactByEmail"
                onChange={(e: any) => {
                  if (e.target.checked) {
                    updateForm({ contactBy: PREFERRED_COMMUNICATION_TYPES.EMAIL });
                  }
                }}
                round={true}
                value="email"
              />
            </div>
            <div className={styles.contactByMail}>
              <Checkbox
                checked={PREFERRED_COMMUNICATION_TYPES.MAIL === contactBy}
                disabled={stepDisabled[2]}
                label="Mail"
                name="contactByMail"
                onChange={(e: any) => {
                  if (e.target.checked) {
                    updateForm({ contactBy: PREFERRED_COMMUNICATION_TYPES.MAIL });
                  }
                }}
                round={true}
                value="mail"
              />
            </div>
          </div>
          <Input containerClass={stepClasses('', 2)} disabled={stepDisabled[2]} label={'Email'} name={'contactEmail'} onChange={handleInputChange} value={contactEmail} />
          <PhoneInput containerClass={stepClasses('', 2)} disabled={stepDisabled[2]} label={'Phone'} required={true} name={'contactPhone'} onChange={handleInputChange} value={contactPhone} />
          <div className={stepClasses(styles.subHeaderContainer, 3)}>
            <h2>Mailing Address</h2>
          </div>
          <div className={stepClasses(styles.addressContainer, 3)}>
            <div className={styles.addressInputContainer}>
              <Input disabled={stepDisabled[3]} label={'Address'} name={'addressLine1'} onChange={handleInputChange} required={true} value={addressLine1} />
            </div>
            <div className={styles.addressOptionalContainer}>
              <Input disabled={stepDisabled[3]} label={'Apt, Suite, etc.'} name={'addressLine2'} onChange={handleInputChange} value={addressLine2} />
            </div>
          </div>
          <Input containerClass={stepClasses('', 3)} disabled={3 > currentStep} label={'City'} name={'addressCity'} onChange={handleInputChange} required={true} value={addressCity} />
          <div className={stepClasses(styles.stateZIPContainer, 3)}>
            <StateSelect disabled={stepDisabled[3]} onChange={handleInputChange} />
            <div className={styles.zipContainer}>
              <Input disabled={stepDisabled[3]} label={'Zip'} name={'addressZIP'} onChange={handleInputChange} required={true} value={addressZIP} />
            </div>
          </div>
          <p className={stepClasses(styles.acceptDisclaimerText, 4)}>
            * I declare under penalty of perjury under United States law that I am the individual whose name and identifying information has been provided in this request form and that all the
            information provided is true, correct, and complete.
          </p>
          <div className={stepClasses(styles.acceptDisclaimerCheckbox, 4)}>
            <Checkbox
              checked={acceptDisclaimer}
              disabled={stepDisabled[4]}
              label="Accept"
              name="acceptDisclaimer"
              onChange={(e: any) => {
                updateForm({ acceptDisclaimer: !acceptDisclaimer });
              }}
              value="accepted"
            />
          </div>
          <div className={styles.submitButtonsContainer}>
            <Button active={false} disabled={stepDisabled[5]} onClick={submitForm} text={'Submit'} type={'Submit'} />
            <Button active={false} disabled={false} link={process.env.REACT_APP_RETURN_URL} onClick={() => null} text={'Cancel'} type={'Cancel'} />
          </div>
        </div>
        <Disclaimer disclaimer={thisDisclaimer} />
      </div>
    </div>
  );
};

export default observer(CCPAForm);
